/**
 * Real-time Notification Service for Unlock Request Status Changes
 * Task 29: Implement real-time notifications for unlock request status changes
 * 
 * This service provides real-time notifications for unlock request status changes
 * using polling with smart intervals and WebSocket-like behavior
 */

import axios from 'axios';

class NotificationService {
  constructor() {
    this.listeners = new Set();
    this.pollingInterval = null;
    this.isPolling = false;
    this.lastNotificationId = null;
    this.basePollingInterval = 30000; // 30 seconds default (reduced load)
    this.maxPollingInterval = 300000; // 5 minutes max
    this.currentPollingInterval = this.basePollingInterval;
    this.retryCount = 0;
    this.maxRetries = 3;
  }

  /**
   * Start real-time notification polling
   */
  startRealTimeNotifications() {
    if (this.isPolling) return;
    
    console.log('🔔 Starting real-time notifications service');
    this.isPolling = true;
    this.currentPollingInterval = this.basePollingInterval;
    this.retryCount = 0;
    this._startPolling();
  }

  /**
   * Stop real-time notification polling
   */
  stopRealTimeNotifications() {
    console.log('⏹️ Stopping real-time notifications service');
    this.isPolling = false;
    if (this.pollingInterval) {
      clearTimeout(this.pollingInterval);
      this.pollingInterval = null;
    }
  }

  /**
   * Subscribe to notification updates
   * @param {Function} callback - Callback function to handle new notifications
   * @returns {Function} Unsubscribe function
   */
  subscribe(callback) {
    this.listeners.add(callback);
    console.log(`📡 Notification listener added. Total listeners: ${this.listeners.size}`);
    
    // Auto-start polling when first listener is added
    if (this.listeners.size === 1) {
      this.startRealTimeNotifications();
    }
    
    // Return unsubscribe function
    return () => {
      this.listeners.delete(callback);
      console.log(`📡 Notification listener removed. Total listeners: ${this.listeners.size}`);
      
      // Auto-stop polling when no listeners left
      if (this.listeners.size === 0) {
        this.stopRealTimeNotifications();
      }
    };
  }

  /**
   * Notify all listeners of new notifications
   * @param {Array} notifications - New notifications array
   */
  _notifyListeners(notifications) {
    this.listeners.forEach(callback => {
      try {
        callback(notifications);
      } catch (error) {
        console.error('Error in notification listener:', error);
      }
    });
  }

  /**
   * Internal polling implementation
   */
  async _startPolling() {
    if (!this.isPolling) return;

    try {
      const notifications = await this._fetchLatestNotifications();
      
      // Check for new notifications
      if (notifications && notifications.length > 0) {
        const hasNewNotifications = this._checkForNewNotifications(notifications);
        
        if (hasNewNotifications) {
          console.log(`🔔 ${notifications.length} new notification(s) received`);
          this._notifyListeners(notifications);
          
          // Show browser notification for unlock request updates
          this._showBrowserNotificationForUnlockRequests(notifications);
        }
        
        // Reset polling interval on successful fetch
        this.currentPollingInterval = this.basePollingInterval;
        this.retryCount = 0;
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
      this._handlePollingError();
    }

    // Schedule next poll
    if (this.isPolling) {
      this.pollingInterval = setTimeout(() => {
        this._startPolling();
      }, this.currentPollingInterval);
    }
  }

  /**
   * Fetch latest notifications from API
   * @returns {Promise<Array>} Array of notifications
   */
  async _fetchLatestNotifications() {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      console.warn('No access token available for notifications');
      return [];
    }

    const config = {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      }
    };

    // Use window.AMS_CONFIG for production compatibility
    const BACKEND_API_URL = window.AMS_CONFIG?.API_URL || 'http://localhost:8000';
    const API_PATH = window.AMS_CONFIG?.API_PATH || '/api';
    const API_BASE_URL = `${BACKEND_API_URL}${API_PATH}`;
    
    // Fetch only unread notifications for efficiency
    const response = await axios.get(`${API_BASE_URL}/notifications/unread/`, config);
    
    if (response.data.results && Array.isArray(response.data.results)) {
      return response.data.results;
    } else if (Array.isArray(response.data)) {
      return response.data;
    }
    
    return [];
  }

  /**
   * Check if there are new notifications since last fetch
   * @param {Array} notifications - Current notifications
   * @returns {boolean} True if there are new notifications
   */
  _checkForNewNotifications(notifications) {
    if (!notifications || notifications.length === 0) {
      // If no notifications, update last seen and don't trigger new notification
      return false;
    }
    
    // On first run, consider all as new
    if (this.lastNotificationId === null) {
      this.lastNotificationId = notifications[0]?.id;
      return true;
    }
    
    // Check if there are notifications newer than last seen
    const hasNew = notifications.some(notif => notif.id > this.lastNotificationId);
    
    if (hasNew) {
      this.lastNotificationId = Math.max(...notifications.map(n => n.id));
    }
    
    return hasNew;
  }

  /**
   * Handle polling errors with exponential backoff
   */
  _handlePollingError() {
    this.retryCount++;
    
    if (this.retryCount <= this.maxRetries) {
      // Exponential backoff: double the interval
      this.currentPollingInterval = Math.min(
        this.currentPollingInterval * 2,
        this.maxPollingInterval
      );
      console.log(`⚠️ Polling error, retrying in ${this.currentPollingInterval}ms (attempt ${this.retryCount}/${this.maxRetries})`);
    } else {
      // Max retries reached, stop polling temporarily
      console.error('❌ Max polling retries reached, stopping notifications');
      this.stopRealTimeNotifications();
      
      // Retry after 5 minutes
      setTimeout(() => {
        if (this.listeners.size > 0) {
          console.log('🔄 Retrying notification service after cooldown');
          this.startRealTimeNotifications();
        }
      }, 300000); // 5 minutes
    }
  }

  /**
   * Show browser notification for unlock request status changes
   * @param {Array} notifications - New notifications
   */
  _showBrowserNotificationForUnlockRequests(notifications) {
    // Request permission if needed
    if (Notification.permission === 'default') {
      Notification.requestPermission();
    }
    
    if (Notification.permission === 'granted') {
      notifications
        .filter(notif => notif.entity_type === 'UnlockRequest')
        .forEach(notif => {
          const browserNotification = new Notification(notif.title, {
            body: notif.message,
            icon: '/favicon.ico',
            tag: `unlock-request-${notif.entity_id}`,
            requireInteraction: true
          });
          
          browserNotification.onclick = () => {
            window.focus();
            // Navigate to settings notifications tab
            if (window.location.pathname !== '/settings') {
              window.location.href = '/settings?tab=notifications';
            }
            browserNotification.close();
          };
          
          // Auto-close after 10 seconds
          setTimeout(() => {
            browserNotification.close();
          }, 10000);
        });
    }
  }

  /**
   * Manually trigger notification check (for user actions)
   */
  async checkForUpdates() {
    if (!this.isPolling) return;
    
    try {
      const notifications = await this._fetchLatestNotifications();
      if (notifications && notifications.length > 0) {
        const hasNew = this._checkForNewNotifications(notifications);
        if (hasNew) {
          this._notifyListeners(notifications);
        }
      }
    } catch (error) {
      console.error('Error in manual notification check:', error);
    }
  }

  /**
   * Mark notification as read
   * @param {number} notificationId - ID of notification to mark as read
   */
  async markAsRead(notificationId) {
    try {
      const token = localStorage.getItem('accessToken');
      const config = {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      };
      
      // Use window.AMS_CONFIG for production compatibility
      const BACKEND_API_URL = window.AMS_CONFIG?.API_URL || 'http://localhost:8000';
      const API_PATH = window.AMS_CONFIG?.API_PATH || '/api';
      const API_BASE_URL = `${BACKEND_API_URL}${API_PATH}`;
      await axios.post(`${API_BASE_URL}/notifications/${notificationId}/mark_read/`, {}, config);
      
      // Trigger immediate update check after marking as read
      setTimeout(() => {
        this.checkForUpdates();
      }, 100);
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  }

  /**
   * Mark all notifications as read
   */
  async markAllAsRead() {
    try {
      const token = localStorage.getItem('accessToken');
      const config = {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      };
      
      // Use window.AMS_CONFIG for production compatibility
      const BACKEND_API_URL = window.AMS_CONFIG?.API_URL || 'http://localhost:8000';
      const API_PATH = window.AMS_CONFIG?.API_PATH || '/api';
      const API_BASE_URL = `${BACKEND_API_URL}${API_PATH}`;
      await axios.post(`${API_BASE_URL}/notifications/mark_all_read/`, {}, config);
      
      // Clear notifications immediately and notify listeners
      this._notifyListeners([]);
      
      // Trigger update check after marking all as read
      this.checkForUpdates();
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  }

  /**
   * Get current notification stats
   */
  getStats() {
    return {
      isPolling: this.isPolling,
      listeners: this.listeners.size,
      currentInterval: this.currentPollingInterval,
      retryCount: this.retryCount,
      lastNotificationId: this.lastNotificationId
    };
  }
}

// Create singleton instance
const notificationService = new NotificationService();

export default notificationService; 